/**
 * Login feature strings.
 *
 * Namespace: login
 */
export const login = {
  // Page
  page: {
    title: "Bejelentkezés",
    subtitle: "Jelentkezzen be a fiókjába",
    subtitleReturn: "Jelentkezzen be a folytatáshoz",
    subtitleFirstLogin: "Első bejelentkezés",
    noAccount: "Nincs még fiókja?",
    createAccount: "Regisztráljon",
    forgotPassword: "Elfelejtett jelszó?",
  },

  // Email step
  email: {
    label: "E-mail cím",
  },

  // Whitelist check step
  whitelistCheck: {
    continue: "Tovább e-mail címmel",
    checking: "Ellenőrzés…",
    error: "Hiba történt. Kérjük, próbálja újra.",
  },

  // Tenant picker step (pre-auth: choose which organization to sign in to)
  tenant: {
    title: "Válasszon szervezetet",
    subtitle: "Válassza ki, melyik szervezetbe szeretne bejelentkezni",
    change: "Másik szervezet választása",
    searchPlaceholder: "Szervezet keresése…",
    searchLabel: "Szervezet keresése",
    noMatches: "Nincs a keresésnek megfelelő szervezet.",
    signingInTo: "Bejelentkezés a következő szervezetbe:",
    // A126: each row states what happens next, since the e-mail-first lookup already knows it.
    actionLogin: "Bejelentkezés",
    actionRegister: "Regisztráció",
    // A145: the personal tenant is the person's own account. These strings never call it a tenant
    // or an organisation. The picker, the back link and the context line use them only when the
    // personal option is present or chosen.
    personal: {
      pickerTitle: "Melyik fiókjával szeretne bejelentkezni?",
      pickerSubtitle: "Ezzel az e-mail-címmel több fiókja is van.",
      // A row of the picker offers a registration, so not every row is an existing account.
      pickerSubtitleWithRegistration: "Ezzel az e-mail-címmel több helyen is folytathatja.",
      organisationsLabel: "Szervezetek",
      divider: "vagy",
      optionTitle: "Személyes fiók",
      optionDescription: "Az Ön saját tere, nem tartozik szervezethez.",
      change: "Másik fiók választása",
      signingIn: "Bejelentkezés a személyes fiókjába",
    },
  },

  // A126: the e-mail-first tenant lookup answers with zero matches — a complete, successful "no",
  // never an error.
  denied: {
    title: "Nincs hozzáférés",
    message:
      "Ezzel az e-mail címmel jelenleg egyetlen szervezethez sem tartozik hozzáférése ehhez az alkalmazáshoz.",
    backToLogin: "Vissza a bejelentkezéshez",
  },

  // A145: the personal tenant would take the address, but registration with it is not open (the
  // address is not Gmail and no institution accepts it). Shown on the e-mail step; the field stays
  // editable. Product reviews the copy.
  closed: {
    addressNotOpen:
      "Ezzel az e-mail-címmel egyelőre nem lehet regisztrálni. Próbálja meg egy másik e-mail-címmel, például Gmail-címmel.",
  },

  // A126: the e-mail-first tenant lookup itself could not answer — these keep the person on the
  // e-mail step, distinct from the denied step above.
  tenantLookup: {
    throttled: "Túl sok próbálkozás történt. Kérjük, próbálja meg néhány perc múlva.",
    technicalError: "Hiba történt az ellenőrzés során. Kérjük, próbálja újra.",
  },

  // A108 S7 fix round 1: shown instead of the credential form when no organisation can be
  // determined at all (no URL param, nothing captured in session) — mirrors
  // forgotPassword.noApplicationContext's card/title mechanism, but with its own message: the
  // forgot-password copy tells the visitor to use "Forgot password", which is wrong guidance for
  // someone trying to sign in.
  noApplicationContext: {
    title: "A szervezet nem azonosítható",
    message:
      "A bejelentkezést abból az alkalmazásból kell elindítania, amelyhez a fiókja tartozik. Nyissa meg az alkalmazás bejelentkezési oldalát, és onnan jelentkezzen be.",
  },

  // Registration step
  register: {
    helperText: "Adja meg a nevét az aktiváló e-mail küldéséhez.",
    // A145: shown only when the person registers into the personal tenant. It states what the
    // person creates and promises nothing more.
    personalNotice: "Személyes fiókot hoz létre.",
    familyName: "Vezetéknév",
    familyNamePlaceholder: "pl. Kovács",
    givenName: "Keresztnév",
    givenNamePlaceholder: "pl. János",
    submit: "Aktiváló e-mail küldése",
    errors: {
      familyNameRequired: "A vezetéknév megadása kötelező.",
      givenNameRequired: "A keresztnév megadása kötelező.",
      apiError: "Hiba történt. Kérjük, próbálja újra.",
      // A145: the MTP create refused a personal registration (User.RegistrationNotOpen /
      // User.RegistrationClosed from the LoginApp).
      addressNotOpen:
        "Ezzel az e-mail-címmel egyelőre nem lehet regisztrálni. Próbálja meg egy másik e-mail-címmel, például Gmail-címmel.",
      temporarilyClosed: "A regisztráció átmenetileg szünetel. Kérjük, próbálja újra később.",
    },
  },

  // Sent step
  sent: {
    title: "E-mail elküldve!",
    bodyPrefix: "Elküldtük az aktiváló e-mailt a(z)",
    bodySuffix: "címre. Keresse meg a postaládájában, és kattintson az aktiválási linkre.",
    backToLogin: "Vissza a bejelentkezéshez",
  },

  // Form fields
  form: {
    emailLabel: "E-mail cím",
    emailPlaceholder: "példa@example.com",
    passwordLabel: "Jelszó",
    passwordPlaceholder: "••••••••",
    submitButton: "Bejelentkezés",
    loadingButton: "Bejelentkezés…",
    enterPasswordFor: "Írja be a jelszavát ehhez a fiókhoz",
    editEmail: "Módosítás",
  },

  // External login (IdP)
  external: {
    divider: "vagy",
    kreta: "Belépés Kréta-val",
    google: "Belépés Google-lel",
    elte: "Belépés ELTE-vel",
  },

  // OAuth callback page
  callback: {
    loadingTitle: "Bejelentkezés folyamatban",
    loadingMessage: "Kérjük, várjon egy pillanatot, miközben befejezzük a bejelentkezést.",
    errorTitle: "Sikertelen bejelentkezés",
    missingParamsError: "Hiányzó azonosító paraméterek. Kérjük, próbálja újra.",
    backToLogin: "Vissza a bejelentkezéshez",
  },

  // Errors
  errors: {
    recaptchaFailed: "reCAPTCHA ellenőrzés sikertelen. Kérjük, próbálja újra.",
    invalidCredentials:
      "Helytelen e-mail cím vagy jelszó. Kérjük, ellenőrizze az adatokat és próbálja újra.",
    accountTemporarilyLocked: "A fiók átmenetileg zárolva van. Próbálkozzon újra később.",
    accountPermanentlyLocked: "A fiók zárolva van. Keresse az adminisztrátort.",
    externalLoginFailed: {
      google: "Nem sikerült belépni Google fiókkal. Kérjük, próbálja újra.",
      kreta: "Nem sikerült belépni Kréta fiókkal. Kérjük, próbálja újra.",
      elte: "Nem sikerült belépni ELTE fiókkal. Kérjük, próbálja újra.",
      default: "Nem sikerült a külső bejelentkezés. Kérjük, próbálja újra.",
    },
  },

  // Success
  success: {
    title: "Sikeres bejelentkezés",
    message: "Sikeresen bejelentkezett. Bezárhatja ezt az ablakot.",
  },
} as const;
